/**
 * 行動プランの指標 — which 指標 a plan's actions are written for (齋藤さん 2026-10-16).
 *
 * Order = importance: 追加KGI first, then 注力① ② ③ (same as the AI dossier line
 * 「行動プランの指標（重要な順）」 in kpiSnapshot). 売上 is the KGI and never gets an action.
 * Each action may carry `focusKey`; plans saved before this have none and stay a flat list.
 */
import { PROPOSED_ACTIONS } from '../components/kuroco/goalEditUtils';
import { SUB_KGI_KEYS, getFocusDef } from './kpiFocusCatalog';
import { MAX_PLAN_ACTIONS } from './kpiSnapshot';

export { MAX_PLAN_ACTIONS };

/**
 * Ordered, de-duplicated 指標 keys: 追加KGI, then 注力 keys in slot order.
 * A 注力 equal to the 追加KGI or to an earlier slot is skipped (the save rules forbid both).
 */
export function actionTargetKeys(subKgiKey, focusKeys = []) {
  const out = [];
  const add = (k) => {
    if (k && k !== 'totalSales' && getFocusDef(k) && !out.includes(k)) out.push(k);
  };
  if (SUB_KGI_KEYS.includes(subKgiKey)) add(subKgiKey);
  focusKeys.forEach(add);
  return out;
}

/** Same list from a saved goals doc (Home / 設定 / 管理画面). */
export function goalsActionTargetKeys(goals) {
  return actionTargetKeys(goals?.subKgiKey, [goals?.focusKey, goals?.subFocusKey, goals?.thirdFocusKey]);
}

/** 「指名率（KGI）」 / 「客単価」 */
export function actionTargetLabel(key, subKgiKey) {
  const label = getFocusDef(key)?.label || key;
  return key && key === subKgiKey ? `${label}（KGI）` : label;
}

/**
 * Actions grouped under their 指標, in importance order — or null when the plan cannot be
 * shown that way: no 指標, or any action untagged / tagged with a key that is not a current
 * 指標 (legacy plan, or 注力 changed since). Null → render the flat list as before.
 */
export function groupActionsByTarget(actions, targetKeys) {
  const list = Array.isArray(actions) ? actions : [];
  if (!list.length || !targetKeys?.length) return null;
  if (!list.every((a) => targetKeys.includes(a?.focusKey))) return null;
  return targetKeys
    .map((key) => ({ key, actions: list.filter((a) => a.focusKey === key) }))
    .filter((g) => g.actions.length > 0);
}

/**
 * Offline fallback when the AI gives nothing: one canned action per 指標 (1 指標 → up to 3),
 * tagged. Only 指標 that have canned lines get one; [] when none do (caller keeps its old set).
 */
export function fallbackActionsFor(targetKeys) {
  const keys = targetKeys || [];
  const rows =
    keys.length === 1
      ? (PROPOSED_ACTIONS[keys[0]] || []).map((a) => ({ ...a, focusKey: keys[0] }))
      : keys.flatMap((k) => (PROPOSED_ACTIONS[k]?.[0] ? [{ ...PROPOSED_ACTIONS[k][0], focusKey: k }] : []));
  return rows.slice(0, MAX_PLAN_ACTIONS).map((a, i) => ({ ...a, id: i + 1 }));
}

/**
 * The 【改善相談】 request GoalEdit sends for a new plan. The count rule itself lives in the
 * prompt (chat_orchestrator); this names the 指標 so the model cannot miss them.
 */
export function actionsRequestText(targetKeys, subKgiKey, currentNames = []) {
  const keys = targetKeys || [];
  const labels = keys.map((k) => `${actionTargetLabel(k, subKgiKey)}(${k})`);
  const ask =
    keys.length >= 2
      ? `指標「${labels.join(' → ')}」それぞれに1つずつ、この順番で毎日の行動を提案してください。`
      : keys.length === 1
        ? `指標「${labels[0]}」を伸ばす毎日の行動を1〜3つ提案してください。`
        : '目標達成につながる毎日の行動を1〜3つ提案してください。';
  const avoid = currentNames.length ? `いまの案「${currentNames.join(' / ')}」とは別の内容にしてください。` : '';
  return `【改善相談】${ask}${avoid}`;
}
